import type { CSSProperties } from '@linaria/core';
import { cx } from '@linaria/core';
import { chunk } from 'lodash-es';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { VList, VListHandle } from 'virtua';
import { SearchResultEntry, SearchResultEntryWithType } from '../../../../types/state';
import { GridLocation, SettingsType } from '../../../constants/enums';
import { AppStateType, useAppStateSelector } from '../../Hooks/useAppState';
import { useIsomorphicLayoutEffect } from '../../Hooks/useIsomorphicLayoutEffect';
import { usePreservePageState } from '../../Hooks/usePreservePageState';
import { usePrevious } from '../../Hooks/usePrevious';
import { useRouteSelector } from '../../Hooks/useRoute';
import { useSettings } from '../../Hooks/useSettings';
import RenderRecommendationsResult from './RenderRecommendationsResult';
import "./SearchRecommendationsGrid.linaria.module.css!=!./SearchRecommendationsGrid?./SearchRecommendationsGrid.linaria.module.css?css=H4sIAAAAAAAAA22M0Q6CIBiF732PtrrQCdNW+jCMCQEFkoCgMd+9meWydfef/zvny7BTiEIEtO009JFTwbirQJ7v6inJZurHlQZBHN/AeUpuZSfwZ+qx2afpEg5fDvPonYFvxVJ63WvnekSAnRQvTFSiTTe6+WF0+KNlTZmzYqNttOxV+2M/WwTcEISXkQh7l3isLpIO9ZQ8ATBgYucDAQAA"
const rootStyles = "atm_e2_1osqo2v atm_vy_1osqo2v";
const stylesWidthAndHeight = "atm_e2_1dk5qia atm_vy_rzutr2";
const resultContainerStyles = "atm_j6_1g8mh4r atm_vy_gc50g4";
const stylesRowRenderer = "atm_j6_1g8mh4r atm_9s_1txwivl";
interface ISearchRecommendationsGrid {
  height: number;
  width: number;
  cellWidth: number;
  columnCount: number;
  columnWidth: number;
  minRowHeight: number;
  models: Array<SearchResultEntry | SearchResultEntryWithType>;
  onOpenMenu(slug: string, options?: any): void;
  onOpenRoom(slug: string): void;
  onTagClick?(tag: string): void;
  onScroll?(offset: number): void;
  searchString: string;
  source: string;
  altDisplay?: boolean;
}
const SearchRecommendationsGrid = memo((props: ISearchRecommendationsGrid) => {
  const {
    columnCount,
    columnWidth,
    minRowHeight,
    models,
    onOpenMenu,
    onOpenRoom,
    onScroll: onScrollProp,
    searchString,
    source,
    height,
    width,
    cellWidth,
    altDisplay,
    onTagClick: onTagClickProp
  } = props;
  const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile');
  const darkMode = useAppStateSelector(AppStateType.APP, 'darkMode');
  const [settings] = useSettings(SettingsType.DEFAULT);
  const [activeID, setActiveID] = useState(null);
  const listRef = useRef<VListHandle>(null);
  const refScrollTop = useRef(0);
  const {
    type,
    query
  } = useRouteSelector('baseQuery');
  const searchKey = `${source}:${searchString ?? ''}`;
  const previousSearchKey = usePrevious(searchKey);
  const onScroll = useCallback((scrollTop: number) => {
    refScrollTop.current = scrollTop;
    onScrollProp?.(scrollTop);
  }, [onScrollProp]);
  const onTagClick = useCallback((tag: string) => {
    if (onTagClickProp) {
      onTagClickProp(tag);
      listRef.current?.scrollTo(0);
    }
  }, [onTagClickProp]);
  const onHover = useCallback((id: number, wipe?: boolean) => {
    setActiveID(wipe ? null : id);
  }, []);
  usePreservePageState({
    gridLocation: GridLocation.Search,
    listRef,
    refScrollTop,
    displayMode: settings.display,
    exploreTab: type ? type : undefined,
    searchQuery: query ? query : undefined
  });
  useIsomorphicLayoutEffect(() => {
    if (previousSearchKey !== null && previousSearchKey !== searchKey) {
      listRef.current?.scrollTo(0);
    }
  }, [previousSearchKey, searchKey]);
  const rowData = useMemo(() => chunk(models, columnCount), [models, columnCount]);
  const rowRenderer = (row, index: number) => {
    return <div className={stylesRowRenderer} key={index}>
                {row.map((model, colIndex) => {
        return <div key={colIndex} className={resultContainerStyles}>
                            <RenderRecommendationsResult onTagClick={onTagClick} darkMode={darkMode} isMobile={isMobile} model={model} onHover={onHover} onOpenMenu={onOpenMenu} onOpenRoom={onOpenRoom} searchString={searchString} settings={settings} source={source} activeID={activeID} cellWidth={cellWidth} altDisplay={altDisplay} />
                        </div>;
      })}
            </div>;
  };
  return <div className={rootStyles}>
            <VList className={cx('no-scrollbars', stylesWidthAndHeight)} style={{
      '--height': `${height}px`,
      '--width': `${width}px`,
      '--column-width': `${columnWidth}px`,
      '--min-row-height': `${minRowHeight}px`
    } as CSSProperties} onScroll={onScroll} data={rowData} ref={listRef}>
                {rowRenderer}
            </VList>
        </div>;
});
export default SearchRecommendationsGrid;